import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, Trash2, Edit2, X, Loader2 } from "lucide-react";

export default function AdminDealers() {
  const [dealers, setDealers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/dealers");
    setDealers(r.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (d) => {
    if (!confirm(`Händler "${d.name}" löschen?`)) return;
    await api.delete(`/dealers/${d.id}`);
    load();
  };

  return (
    <div className="p-10 max-w-6xl" data-testid="admin-dealers-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Administration</div>
          <h1 className="text-3xl font-semibold tracking-tight">Händler</h1>
        </div>
        <button onClick={() => { setEditing(null); setShow(true); }} data-testid="add-dealer-button"
                className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2.5 rounded-sm flex items-center gap-2">
          <Plus size={14}/> Neuer Händler
        </button>
      </div>
      <div className="border border-zinc-200">
        {loading ? <div className="p-8 text-center text-sm text-zinc-500">Wird geladen...</div> : dealers.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500" data-testid="dealers-empty">Noch keine Händler. Fügen Sie den ersten Händler hinzu.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium">Name</th>
                <th className="text-left px-5 py-2.5 font-medium">Ansprechpartner</th>
                <th className="text-left px-5 py-2.5 font-medium">E-Mail</th>
                <th className="text-left px-5 py-2.5 font-medium">Telefon</th>
                <th className="text-right px-5 py-2.5 font-medium w-24">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {dealers.map((d) => (
                <tr key={d.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-5 py-3 font-medium">{d.name}</td>
                  <td className="px-5 py-3 text-zinc-600">{d.contactName || "—"}</td>
                  <td className="px-5 py-3 font-mono text-xs text-zinc-600">{d.email || "—"}</td>
                  <td className="px-5 py-3 text-zinc-600">{d.phone || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => { setEditing(d); setShow(true); }} className="text-zinc-500 hover:text-zinc-900 mr-3">
                      <Edit2 size={14}/>
                    </button>
                    <button onClick={() => onDelete(d)} className="text-zinc-400 hover:text-red-600">
                      <Trash2 size={14}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {show && <DealerModal dealer={editing} onClose={() => setShow(false)} onSaved={load} />}
    </div>
  );
}

function DealerModal({ dealer, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: dealer?.name || "", contactName: dealer?.contactName || "",
    email: dealer?.email || "", phone: dealer?.phone || "",
    address: dealer?.address || "", notes: dealer?.notes || "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (dealer) await api.patch(`/dealers/${dealer.id}`, form);
      else await api.post("/dealers", form);
      onSaved(); onClose();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="dealer-modal">
      <div className="bg-white max-w-md w-full border border-zinc-200">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
          <h3 className="font-semibold tracking-tight">{dealer ? "Händler bearbeiten" : "Neuer Händler"}</h3>
          <button onClick={onClose}><X size={16} className="text-zinc-500"/></button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          {[
            ["name", "Firma *", true],
            ["contactName", "Ansprechpartner"],
            ["email", "E-Mail"],
            ["phone", "Telefon"],
            ["address", "Adresse"],
          ].map(([k, label, req]) => (
            <div key={k}>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">{label}</label>
              <input value={form[k]} onChange={(e) => setForm({...form, [k]: e.target.value})} required={req}
                    data-testid={`dealer-form-${k}`}
                    className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm"/>
            </div>
          ))}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Notiz</label>
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2}
                     className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm"/>
          </div>
          {error && <div className="text-xs text-red-600 bg-red-50 px-3 py-2">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="border border-zinc-300 text-sm px-4 py-2 hover:bg-zinc-50">Abbrechen</button>
            <button type="submit" disabled={saving} data-testid="dealer-form-submit"
                    className="bg-zinc-900 text-white text-sm font-medium px-4 py-2 hover:bg-zinc-800 flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin"/>} Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
